import { useEffect, useState } from 'preact/hooks';
import { googleSignInUrl, keepReturnTo, saveSession } from '../auth.js';
import { DEV_LOGIN, WORKSPACE_DOMAIN } from '../config.js';
import { rememberLang, texts, useLang, type Lang } from '../i18n.js';
import { EmergencyLinks } from '../emergency.js';
import { LegalLinks } from './Legal.js';
import { createApi } from '../api.js';
import { PasswordSignin } from './PasswordSignin.js';
import { AccessRequestForm } from './accessRequest.js';

/** Why the sign-in page is showing, beyond "not signed in yet". */
export type LoginNotice =
  | { kind: 'wrong_account'; email?: string }
  | { kind: 'not_invited' }
  | { kind: 'signed_out' }
  | { kind: 'cancelled' }
  | { kind: 'offline' }
  | { kind: 'error'; message?: string };

const DOMAIN = WORKSPACE_DOMAIN || 'klarafardigafilm.se';

/**
 * One sign-in page for everyone (owner 2026-10-31: "ett gränssnitt", neither purely internal nor external): KFF staff,
 * freelancers and clients. The visitor gives their email and the page offers the ways that fit: Google first for a KFF
 * address, a password, or a code by email (the Hub works out whether the code is a freelancer's or a client's,
 * services/anySignin.ts). The same page shows on /, /t/signin and the portal without a session. Before sign-in there
 * is no person yet, so it follows the language remembered on this device; English unless the browser prefers Swedish.
 */
const TX = texts(
  {
    signedOut: 'You were signed out',
    signedOutBody: 'Your session ended. Sign in to continue where you were.',
    wrongAccount: (email?: string) => (email ? `${email} can't use Google here` : "That Google account can't be used here"),
    wrongAccountBody: (domain: string) => `Google sign-in is for @${domain} accounts. Not on the KFF team? Enter your email below instead.`,
    notInvited: "You don't have access yet",
    notInvitedBody: "Your KFF account isn't set up in the Hub. Ask an admin to add you.",
    failed: "Couldn't sign you in",
    cancelled: 'Google sign-in was cancelled or failed. Try again.',
    offline: "Couldn't reach KFF Hub. Check your connection and try again.",
    generic: 'Something went wrong. Try again.',
    headline: 'Every project, from brief to ',
    headlineEm: 'final cut',
    pitch: "Klara Färdiga Film's place for projects, review and approvals.",
    foot: 'Klara Färdiga Film',
    other: 'Svenska',
    title: 'Sign in to KFF Hub',
    lead: 'One sign-in for everyone who works with KFF.',
    leadGoogle: 'Sign in with your Google account.',
    titleNext: 'Sign in',
    leadStaff: 'KFF accounts sign in with Google.',
    leadBoth: 'Sign in with your password, or get a code by email.',
    leadCode: "We'll email you a code to sign in.",
    opening: 'Opening Google…',
    google: 'Continue with Google',
    or: 'or',
    email: 'Email',
    next: 'Continue',
    change: 'Change',
    sendCode: 'Email me a sign-in code',
    codeHint: 'No password? Clients and freelancers sign in with a code.',
    codeTitle: 'Check your email',
    codeSent: (e: string) => `If ${e} has access to KFF Hub, a six-digit code is on its way. It works for 15 minutes.`,
    code: 'Code',
    signIn: 'Sign in',
    wait: 'Wait…',
    newCode: 'Send a new code',
    back: 'Back',
    newHere: 'New to KFF Hub?',
    askAccess: 'Ask for access',
    help: 'Trouble signing in? Ask your contact at KFF.',
    dev: 'Development sign-in (fictional data, wrangler dev only)',
    devBtn: 'Sign in (dev)'
  },
  {
    signedOut: 'Du har loggats ut',
    signedOutBody: 'Din session tog slut. Logga in för att fortsätta där du var.',
    wrongAccount: (email?: string) => (email ? `${email} kan inte använda Google här` : 'Det Google-kontot kan inte användas här'),
    wrongAccountBody: (domain: string) => `Inloggning med Google är för @${domain}-konton. Inte i KFF-teamet? Skriv din e-post nedan i stället.`,
    notInvited: 'Du har inte tillgång än',
    notInvitedBody: 'Ditt KFF-konto är inte upplagt i Hubben. Be en admin att lägga till dig.',
    failed: 'Det gick inte att logga in',
    cancelled: 'Inloggningen med Google avbröts eller misslyckades. Försök igen.',
    offline: 'Det gick inte att nå KFF Hub. Kontrollera anslutningen och försök igen.',
    generic: 'Något gick fel. Försök igen.',
    headline: 'Varje projekt, från brief till ',
    headlineEm: 'färdig film',
    pitch: 'Klara Färdiga Films plats för projekt, granskning och godkännanden.',
    foot: 'Klara Färdiga Film',
    other: 'English',
    title: 'Logga in i KFF Hub',
    lead: 'En inloggning för alla som jobbar med KFF.',
    leadGoogle: 'Logga in med ditt Google-konto.',
    titleNext: 'Logga in',
    leadStaff: 'KFF-konton loggar in med Google.',
    leadBoth: 'Logga in med ditt lösenord, eller få en kod till mejlen.',
    leadCode: 'Vi mejlar dig en kod att logga in med.',
    opening: 'Öppnar Google…',
    google: 'Fortsätt med Google',
    or: 'eller',
    email: 'E-post',
    next: 'Fortsätt',
    change: 'Ändra',
    sendCode: 'Mejla mig en inloggningskod',
    codeHint: 'Inget lösenord? Kunder och frilansare loggar in med en kod.',
    codeTitle: 'Titta i mejlen',
    codeSent: (e: string) => `Om ${e} har tillgång till KFF Hub är en sexsiffrig kod på väg. Den gäller i 15 minuter.`,
    code: 'Kod',
    signIn: 'Logga in',
    wait: 'Vänta…',
    newCode: 'Skicka en ny kod',
    back: 'Tillbaka',
    newHere: 'Ny i KFF Hub?',
    askAccess: 'Begär åtkomst',
    help: 'Problem att logga in? Fråga din kontakt på KFF.',
    dev: 'Inloggning för utveckling (påhittade data, bara wrangler dev)',
    devBtn: 'Logga in (dev)'
  }
);

function Notice(p: { notice: LoginNotice }) {
  const t = TX[useLang()];
  const n = p.notice;
  if (n.kind === 'signed_out')
    return (
      <div class="login-alert login-alert--info" role="status">
        <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
        <div>
          <strong>{t.signedOut}</strong>
          {t.signedOutBody}
        </div>
      </div>
    );
  const [title, text] =
    n.kind === 'wrong_account'
      ? [t.wrongAccount(n.email), t.wrongAccountBody(DOMAIN)]
      : n.kind === 'not_invited'
        ? [t.notInvited, t.notInvitedBody]
        : [t.failed, n.kind === 'cancelled' ? t.cancelled : n.kind === 'offline' ? t.offline : n.message || t.generic];
  return (
    <div class="login-alert login-alert--crit" role="alert">
      <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7.5V13M12 16.5h.01" /></svg>
      <div>
        <strong>{title}</strong>
        {text}
      </div>
    </div>
  );
}

const GoogleG = () => (
  <svg class="login-g" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);


type Status = { password: boolean; code: boolean; access: boolean };
const isStaff = (email: string) => email.trim().toLowerCase().endsWith(`@${DOMAIN.toLowerCase()}`);
const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** The language picked here follows the visitor into the Hub, a freelancer's session and the portal. */
function keepLang(l: Lang) {
  rememberLang(l);
  try {
    localStorage.setItem('kff.link.lang', l);
    localStorage.setItem('kff.portal.lang', l);
  } catch {
    /* private mode: the choice lasts for this page */
  }
}

/**
 * Signed in with a code: a freelancer's session opens the Hub (/t/{token}, App keeps it), a client's the portal
 * (/portal/{token}, which opens the project or review the visitor came from).
 */
function openCodeSession(r: { kind: 'crew' | 'portal'; token: string }) {
  if (r.kind === 'crew') history.replaceState(null, '', `/t/${r.token}`);
  else {
    const m = /^\/portal\/(p|r)\/([A-Za-z0-9-]{1,40})/.exec(location.pathname);
    history.replaceState(null, '', `/portal/${r.token}${m ? `?${m[1]}=${m[2]}` : ''}`);
  }
  dispatchEvent(new PopStateEvent('popstate'));
}

function CodeStep(p: { email: string; onBack: () => void }) {
  const lang = useLang();
  const t = TX[lang];
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const digits = code.replace(/\s/g, '');
  const api = createApi(null);
  const again = async () => {
    setBusy(true);
    setErr('');
    try {
      await api.post('/api/signin/code', { email: p.email, lang });
    } catch (e) {
      setErr(errText(e));
    }
    setBusy(false);
  };
  const verify = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      openCodeSession(await api.post<{ kind: 'crew' | 'portal'; token: string }>('/api/signin/verify', { email: p.email, code: digits }));
    } catch (x) {
      setErr(errText(x));
      setBusy(false);
    }
  };
  return (
    <form class="login-form" onSubmit={(e) => void verify(e)}>
      <p class="login-lead" role="status">
        {t.codeSent(p.email)}
      </p>
      <div class="login-field">
        <label class="label" for="lg-code">
          {t.code}
        </label>
        <input id="lg-code" class="input portal-code" inputMode="numeric" autocomplete="one-time-code" maxLength={7} required value={code} onInput={(e) => setCode((e.target as HTMLInputElement).value)} />
      </div>
      {err ? (
        <p class="login-error" role="alert">
          {err}
        </p>
      ) : null}
      <button type="submit" class="btn btn-primary login-submit" disabled={busy || !/^\d{6}$/.test(digits)}>
        {busy ? t.wait : t.signIn}
      </button>
      <button type="button" class="btn-link login-link" onClick={() => void again()} disabled={busy}>
        {t.newCode}
      </button>
      <button type="button" class="btn-link login-link" onClick={p.onBack}>
        {t.back}
      </button>
    </form>
  );
}

function GoogleButton(p: { label: string; opening: string }) {
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    // back from Google via the back button: the page comes from the bfcache with the spinner still on
    const reset = () => setBusy(false);
    addEventListener('pageshow', reset);
    return () => removeEventListener('pageshow', reset);
  }, []);
  return (
    <a
      class="login-google"
      href={googleSignInUrl(location.origin)}
      aria-busy={busy}
      aria-disabled={busy}
      onClick={(e) => {
        if (busy) return e.preventDefault();
        // from the freelancer or portal address, Google's way in lands on the Hub's start page
        keepReturnTo(/^\/(t|portal)(\/|$)/.test(location.pathname) ? '/' : location.pathname + location.search);
        setBusy(true);
      }}
    >
      {busy ? <span class="login-spinner" aria-hidden="true" /> : <GoogleG />}
      {busy ? p.opening : p.label}
    </a>
  );
}

export function Login(p: { notice?: LoginNotice | null; onLang?: (l: Lang) => void }) {
  const lang = useLang();
  const t = TX[lang];
  const [devEmail, setDevEmail] = useState('admin@example.test');
  const [status, setStatus] = useState<Status>({ password: false, code: false, access: false });
  const [email, setEmail] = useState('');
  // start: Google or the address · next: the ways for that address · code: the emailed code · access: "Begär åtkomst"
  const [step, setStep] = useState<'start' | 'next' | 'code' | 'access'>('start');
  // two-step or "Forgot password" fills the card on its own
  const [away, setAway] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    createApi(null)
      .get<Status>('/api/signin/status')
      .then(setStatus, () => undefined);
  }, []);
  const byEmail = status.password || status.code;
  const staff = isStaff(email);
  const sendCode = async () => {
    setBusy(true);
    setErr('');
    try {
      await createApi(null).post('/api/signin/code', { email: email.trim(), lang });
      setStep('code');
    } catch (e) {
      setErr(errText(e));
    }
    setBusy(false);
  };
  const restart = () => {
    setStep('start');
    setErr('');
    setAway(false);
  };
  const pick = () => {
    const next: Lang = lang === 'sv' ? 'en' : 'sv';
    keepLang(next);
    p.onLang?.(next);
  };
  const shownEmail = email.trim();
  return (
    <div class="login" lang={lang}>
      <aside class="login-side">
        <span class="login-brand">
          <span class="brand-mark" aria-hidden="true">
            KFF
          </span>
          <span aria-hidden="true">Hub</span>
          <span class="sr-only">KFF Hub</span>
        </span>
        <div class="login-pitch">
          <p class="login-headline">
            {t.headline}
            <em>{t.headlineEm}</em>.
          </p>
          <p>{t.pitch}</p>
        </div>
        <p class="login-foot">{t.foot}</p>
      </aside>
      <main class="login-main">
        {p.onLang ? (
          <button type="button" class="login-lang" onClick={pick} lang={lang === 'sv' ? 'en' : 'sv'}>
            {t.other}
          </button>
        ) : null}
        <section class="login-card" aria-labelledby="login-title">
          {step === 'access' ? (
            <AccessRequestForm lang={lang} onBack={restart} />
          ) : (
            <>
              <h1 id="login-title">{step === 'start' ? t.title : step === 'code' ? t.codeTitle : t.titleNext}</h1>
              {step === 'start' ? <p class="login-lead">{byEmail ? t.lead : t.leadGoogle}</p> : null}
              {step === 'next' && !away && (staff || status.code) ? <p class="login-lead">{staff ? t.leadStaff : status.password ? t.leadBoth : t.leadCode}</p> : null}
              {step === 'start' && p.notice ? <Notice notice={p.notice} /> : null}
              {step === 'start' && p.notice && (p.notice.kind === 'offline' || p.notice.kind === 'error') ? <EmergencyLinks lang={lang} /> : null}
              {step !== 'start' && !away ? (
                <div class="login-email">
                  <b>{shownEmail}</b>
                  <button type="button" class="btn-link login-link" onClick={restart}>
                    {t.change}
                  </button>
                </div>
              ) : null}
              {step === 'start' ? (
                <>
                  <GoogleButton label={t.google} opening={t.opening} />
                  {byEmail ? (
                    <form
                      class="login-form"
                      onSubmit={(e) => {
                        e.preventDefault();
                        setErr('');
                        setStep('next');
                      }}
                    >
                      <p class="login-or">
                        <span>{t.or}</span>
                      </p>
                      <div class="login-field">
                        <label class="label" for="lg-email">
                          {t.email}
                        </label>
                        <input id="lg-email" class="input input--wide" type="email" autocomplete="username" required value={email} onInput={(e) => setEmail((e.target as HTMLInputElement).value)} />
                      </div>
                      <button type="submit" class="btn btn-primary login-submit" disabled={!shownEmail}>
                        {t.next}
                      </button>
                    </form>
                  ) : null}
                  {status.access ? (
                    <p class="login-more">
                      {t.newHere}{' '}
                      <button type="button" class="btn-link login-link" onClick={() => setStep('access')}>
                        {t.askAccess}
                      </button>
                    </p>
                  ) : null}
                </>
              ) : step === 'code' ? (
                <CodeStep email={shownEmail} onBack={() => setStep('next')} />
              ) : (
                <>
                  {staff && !away ? <GoogleButton label={t.google} opening={t.opening} /> : null}
                  {status.password ? <PasswordSignin key={shownEmail} email={shownEmail} secondary={staff} onAway={setAway} /> : null}
                  {status.code && !staff && !away ? (
                    <div class={status.password ? 'login-alt' : 'login-form'}>
                      {err ? (
                        <p class="login-error" role="alert">
                          {err}
                        </p>
                      ) : null}
                      <button type="button" class={status.password ? 'btn login-submit' : 'btn btn-primary login-submit'} onClick={() => void sendCode()} disabled={busy}>
                        {busy ? t.wait : t.sendCode}
                      </button>
                      {status.password ? <p class="hint">{t.codeHint}</p> : null}
                    </div>
                  ) : null}
                </>
              )}
            </>
          )}
          <p class="login-help">{t.help}</p>
          <LegalLinks lang={lang} />
          {DEV_LOGIN ? (
            <form
              class="stack blk login-dev"
              onSubmit={(e) => {
                e.preventDefault();
                saveSession({ accessToken: `dev:${devEmail}`, expiresAt: Date.now() + 8 * 3600_000 });
                location.assign('/');
              }}
            >
              <label for="dev-email" class="label">
                {t.dev}
              </label>
              <input id="dev-email" class="input" style="width:100%" value={devEmail} onInput={(e) => setDevEmail((e.target as HTMLInputElement).value)} />
              <button type="submit" class="btn">
                {t.devBtn}
              </button>
            </form>
          ) : null}
        </section>
      </main>
    </div>
  );
}
